<template>
    <div>
        <breadcrumb class="px-4 bg-light pb-0" :items="breadcrumbs" />
        <div class="page-content">
            <h1 class="mx-0">
                {{ details ? details.title : "" }}
            </h1>

            <b-card footer-class="p-0" class="rounded-lg">
                <div class="profile-details">
                    <input
                        type="hidden"
                        name="id"
                        :value="details ? details.id : ''"
                    />

                    <loader-profile
                        v-if="!isDataLoaded"
                        :show-image="false"
                    ></loader-profile>

                    <b-row v-else-if="isDataLoaded && (details || editDetails)">
                        <b-col lg="6">
                            <b-form-group
                                label-for="title"
                                label-cols="3"
                                label="Title:"
                            >
                                <div v-if="isEditMode">
                                    <b-input
                                        id="title"
                                        v-model="editDetails.title"
                                        :class="{
                                            'border-danger':
                                                errors.has('title') ||
                                                editDetails.isTitleExceed
                                        }"
                                        v-validate="'required'"
                                        name="title"
                                        type="text"
                                        placeholder="Title (required)"
                                    />
                                    <span
                                        v-bind:style="[
                                            titleCount >= 0
                                                ? { color: 'grey' }
                                                : { color: 'red' }
                                        ]"
                                        style="font-size: 10px;"
                                        class="mt-1 d-block text-right"
                                    >
                                        {{ titleCount }} character(s) remaining
                                    </span>
                                </div>
                                <span v-else class="d-block pt-2">{{
                                    details.title
                                }}</span>
                            </b-form-group>
                            <b-form-group
                                label-for="client"
                                label-cols="3"
                                label="Client:"
                            >
                                <!-- cannot be edited as for now -->
                                <client-name
                                    v-if="details && details.client"
                                    class="pt-2 d-block"
                                    :client="details.client"
                                />
                            </b-form-group>

                            <b-form-group
                                label-for="patient"
                                label-cols="3"
                                label="Patient:"
                            >
                                <vue-select
                                    v-if="
                                        isEditMode &&
                                        !isWebformSent &&
                                        module != 'patient'
                                    "
                                    id="patient"
                                    :class="{
                                        'border-danger': errors.has('patient')
                                    }"
                                    :options="patientOptions"
                                    v-model="editDetails.patient"
                                    label="name"
                                    name="patient"
                                    aria-placeholder="true"
                                    aria-expanded="true"
                                    placeholder="Patient name (optional)"
                                ></vue-select>

                                <patient-name
                                    v-else-if="details.patient"
                                    class="pt-2 d-block"
                                    :patient="details.patient"
                                />
                            </b-form-group>

                            <b-form-group
                                label-for="create_by"
                                label-cols="3"
                                label="Author:"
                            >
                                <b-input
                                    plaintext
                                    :value="
                                        details.create_by
                                            ? details.create_by.label
                                            : null
                                    "
                                />
                            </b-form-group>

                            <b-form-group
                                v-if="!_.isEmpty(details.soap)"
                                label-for="soap"
                                label-cols="3"
                                label="Reference:"
                            >
                                <span class="d-block pt-2">
                                    <soap-link :soap="details.soap" />
                                </span>
                            </b-form-group>
                        </b-col>
                        <b-col lg="6">
                            <b-form-group
                                label-for="create_at"
                                label-cols="3"
                                label="Created:"
                            >
                                <b-input
                                    plaintext
                                    :value="
                                        _carbonDateDisplay(details.create_at)
                                    "
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="updated"
                                label-cols="3"
                                label="Last Updated:"
                            >
                                <b-input
                                    plaintext
                                    :value="
                                        _carbonDateDisplay(details.update_at)
                                    "
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="sent_date"
                                label-cols="3"
                                label="Sent:"
                            >
                                <b-input
                                    v-if="details.sent_date"
                                    plaintext
                                    :value="
                                        _carbonDateDisplay(details.sent_date)
                                    "
                                />
                            </b-form-group>

                            <b-form-group
                                label-for="submitted_at"
                                label-cols="3"
                                label="Submitted:"
                            >
                                <b-input
                                    v-if="details.submitted_at"
                                    plaintext
                                    :value="
                                        _carbonDateDisplay(details.submitted_at)
                                    "
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <template v-slot:footer no-body v-if="isDataLoaded && details">
                    <webform-creator />
                </template>
            </b-card>
        </div>
        <modal-delete-webform
            ref="modalDeleteWebform"
            @onsuccess="redirectToProfile()"
        />
        <ModalEmail ref="modal-email" @onsuccess="fetchData" />
        <ModalSMS ref="modal-sms" @onsuccess="fetchData" />
    </div>
</template>

<script>
import ClientWebformService from "@/services/client-webform";
import PatientService from "@/services/patient";
import { cloneDeep, find } from "lodash";
import unsavePrompt from "@/mixins/unsave-prompt.js";

export default {
    mixins: [unsavePrompt],
    components: {
        LoaderProfile: () => import("@/components/elements/loader-profile.vue"),
        ClientName: () => import("@/components/commons/client-name"),
        ModalEmail: () => import("@/components/webform/modal-send-email"),
        ModalSMS: () => import("@/components/webform/modal-send-sms"),
        WebformCreator: () => import("@/components/webform/webform-creator"),
        ModalDeleteWebform: () => import("@/components/webform/modal-delete"),
        vueSelect: () => import("vue-select"),
        PatientName: () => import("@/components/commons/patient-name"),
        SoapLink: () => import("@/components/commons/soap-link"),
        Breadcrumb: () => import("@/components/commons/breadcrumb")
    },
    data() {
        return {
            isDataLoaded: false,
            titleCount: 100,
            patientOptions: [],
            isLoadPatientOptions: false
        };
    },
    computed: {
        module() {
            if (this.$route.name == "patient_webform") {
                return "patient";
            }
            return "client";
        },
        details: {
            get() {
                return this.$store.state.webform.details;
            },
            set(value) {
                this.$store.commit("webform/setDetails", value);
            }
        },
        webformContent: {
            get() {
                return this.$store.state.webform.webformContent;
            },
            set(value) {
                this.$store.commit("webform/setWebformContent", value);
            }
        },
        editDetails: {
            get() {
                return this.$store.state.webform.editDetails;
            },
            set(value) {
                this.$store.commit("webform/setEditDetails", value);
            }
        },
        isEditMode: {
            get() {
                return this.$store.state.webform.editMode;
            }
        },
        form_dirty() {
            return this.$store.getters["webform/isDirty"];
        },
        breadcrumbs() {
            let breadcrumbs = [];
            if (this.details) {
                if (this.module == "patient") {
                    breadcrumbs.push(
                        {
                            text: "Patient",
                            to: {
                                name: "patient_dashboard"
                            }
                        },
                        {
                            text: this.details.patient.name,
                            to: {
                                name: "patient_profile",
                                params: {
                                    id: this.details.patient.encoded_id
                                }
                            }
                        }
                    );
                } else if (this.module == "client") {
                    breadcrumbs.push(
                        {
                            text: "Client",
                            to: {
                                name: "client_dashboard"
                            }
                        },
                        {
                            text: this.details.client.label,
                            to: {
                                name: "client_profile",
                                params: {
                                    id: this.details.client.encoded_id
                                }
                            }
                        }
                    );
                }

                breadcrumbs.push({
                    text:
                        this.details.title.length > 60
                            ? this.details.title.substring(0, 60 - 3) + "..."
                            : this.details.title,
                    active: true
                });
            }
            return breadcrumbs;
        },
        isWebformSent() {
            return this.details && this.details.is_sent;
        }
    },
    watch: {
        fields: {
            handler() {
                this.$store.commit("webform/setDirty", this.is_dirty());
            },
            deep: true
        },
        "editDetails.title"(value) {
            if (this.editDetails) {
                let valueLength = 0;
                if (value) {
                    valueLength = value.length;
                }
                this.titleCount = 100 - valueLength;

                if (this.titleCount < 0) {
                    this.editDetails.isTitleExceed = true;
                } else {
                    this.editDetails.isTitleExceed = false;
                }
            }
        },
        isEditMode(value) {
            if (value && !this.isLoadPatientOptions && !this.isWebformSent) {
                this.getPatients();
            }
        }
    },
    mounted() {
        this.fetchData();
    },
    beforeDestroy() {
        // to make sure the button in display mode when re-open webform
        this.resetData();
    },
    methods: {
        resetData() {
            this.details = null;
            this.editDetails = null;
            this.webformContent = null;
            this.$store.commit("webform/setEditMode", false);
            this.$store.commit("webform/setDirty", false);
        },
        async fetchData() {
            try {
                this.isDataLoaded = false;
                let webformEncodedId = this.$route.params.id;

                // reset the webform details
                this.resetData();

                const fetchDetails = await ClientWebformService.getWebformDetail(
                    webformEncodedId,
                    true
                );
                if (fetchDetails) {
                    // TODO - EPIC-1000 - to add the soap encoded id in this API
                    this.$store.dispatch("soap/checkSoapEnableBeta");
                    this.details = fetchDetails;

                    if (fetchDetails.isactive == false) {
                        this._message("The form no longer available", "danger");
                        this.redirectToProfile();
                        return;
                    }

                    this.webformContent = fetchDetails.webform_content;
                    this.editDetails = cloneDeep(fetchDetails);

                    this.isDataLoaded = true;

                    this.dynamicPageTitle(this.details.title);
                } else {
                    throw Error("Form is not available");
                }
            } catch (error) {
                console.error(error);
                this.isDataLoaded = true;
                this._message(error.message, "danger");
                this.redirectToProfile();
            }
        },
        is_dirty() {
            return Object.keys(this.fields).some(key => this.fields[key].dirty);
        },
        redirectToProfile() {
            if (this.module == "patient") {
                const patientEncodedId =
                    this.details && this.details.patient.encoded_id
                        ? this.details.patient.encoded_id
                        : null;
                this.$emit("redirectToPatientProfile", patientEncodedId);
            } else {
                const clientEncodedId =
                    this.details && this.details.client.encoded_id
                        ? this.details.client.encoded_id
                        : null;
                this.$emit("redirectToClientProfile", clientEncodedId);
            }
        },
        deleteWebform(webform) {
            this.$refs.modalDeleteWebform.open(webform);
        },
        sendEmail(webform) {
            this.$refs["modal-email"].open(webform);
        },
        sendSMS(webform) {
            this.$refs["modal-sms"].open(webform);
        },
        getPatients() {
            let params = {
                search_key: null,
                id: this.details.client_id,
                all_status: 1
            };
            PatientService.getPatientsByClient(params).then(response => {
                this.isLoadPatientOptions = true;
                this.patientOptions = response;
                this.editDetails.patient = find(response, {
                    id: this.details.patient_id
                });
            });
        }
    }
};
</script>
<style scoped>
.bv-no-focus-ring {
    display: flex;
    align-items: center;
}
</style>
